// eslint-disable-next-line no-use-before-define
import React, { useEffect, useRef } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import SimpleTableFilter, {
  concatActionDocumentsThenRest,
  DATATYPE,
  documentNeedsAttention,
} from 'shared-components/components/UIFormComponents/SimpleTableFilter';
import DocumentUploadButton from './DocumentUploadButton';
import { CurrentAppConfig } from 'op-pages/RO/Careplan/AppConfig';
import { Stack, styled, Typography } from '@mui/material';
import { Description, Edit, FindInPageOutlined as FindInPageOutlinedIcon } from '@mui/icons-material';

const StyledDocListCol = styled(Stack)`
  flex-grow: 1;
  margin-top: 0.5em;
  color: ${(props) => props.theme.palette.text.primary};
  overflow: auto;
  padding: 0px;

  th {
    white-space: nowrap;
  }
  td {
    font-size: 14px;
  }
  table {
    margin: 0;
  }
`;

const StyledContainer = styled('div')`
  height: 100%;
  margin-bottom: 4px;
  display: flex;
  flex-direction: column;
  tbody tr:hover {
    cursor: pointer;
  }
`;

const StyledSimpleTable = styled(SimpleTableFilter)`
  line-height: 40px;
  margin: 25px 5px;
`;

const HeadingBlock = styled('div')`
  display: flex;
  justify-content: space-between;
`;

interface ROPatientDocsListProps {
  setSelectedDocument: any;
  selectedDocument: any;
  documents: any;
  emptyStateMessage: string;
  documentEditModes: Record<string, boolean>;
  // When false, the list will not auto-select the first document if nothing is
  // selected. This is used by the small-screen modal view where the viewer is
  // opened/closed explicitly: auto-selecting on an empty selection would
  // immediately re-open the first document when the user closes the modal.
  autoSelectFirst?: boolean;
}

const TABLE_HEADER = [
  { header: 'Type', key: 'title', sort: false, type: DATATYPE.STRING },
  { header: 'Date', key: 'dateOfDocument', sort: true, type: DATATYPE.DATE },
];

const isEditableDocument = (doc: any): boolean =>
  Boolean(doc.editableDocumentGroupId) && doc.status?.toLowerCase() !== 'void';

/**
 * Normalises a single document for display:
 * - Draft rows: statusLine shows '-' (Draft subtitle comes from renderRowStatus)
 * - Remaps 'Loading' → 'Approved' (still being synced but functionally approved)
 */
export const normaliseDocument = (doc: any): any => ({
  ...doc,
  statusLabel: doc.status === 'Loading' ? 'Approved' : doc.status,
});

/**
 * Normalises documents for display:
 * - Remove documents with status 'Creating' (transitional backend state, never shown to users)
 * - Remap documents with status 'Loading' to 'Approved' (still being synced but functionally approved)
 * - Sort by dateOfDocument descending
 *   ensuring auto-selection of documents[0] always picks the visually first document in the list.
 */
export const normaliseDocuments = (documents: any[]): any[] =>
  documents
    .map(normaliseDocument)
    .sort((a, b) => new Date(b.dateOfDocument).getTime() - new Date(a.dateOfDocument).getTime());

export { concatActionDocumentsThenRest, documentNeedsAttention };

export const renderDocumentRowIcon = (doc: any): React.ReactNode =>
  isEditableDocument(doc) ? <Edit fontSize="small" /> : <Description fontSize="small" />;

export const renderDocumentRowTooltip = (doc: any): string | undefined =>
  isEditableDocument(doc) ? 'Editable document' : undefined;

const HEADING = 'Documents';

const DocumentsList = (props: ROPatientDocsListProps): JSX.Element => {
  const match = useRouteMatch<any>();
  const history = useHistory();
  const docsListRef = useRef<HTMLDivElement>(null);
  const { id: patientId } = match.params;
  const { documents, setSelectedDocument, selectedDocument, documentEditModes, autoSelectFirst = true } = props;

  const orderedForSelection = concatActionDocumentsThenRest(documents ?? [], documentEditModes);

  React.useEffect(() => {
    if (autoSelectFirst && orderedForSelection?.length && !selectedDocument) {
      setSelectedDocument(orderedForSelection[0]);
    }
  }, [documents, documentEditModes, selectedDocument, setSelectedDocument, autoSelectFirst]);

  return (
    <StyledContainer>
      <HeadingBlock>
        <Typography variant="h6">{HEADING}</Typography>
        {CurrentAppConfig.PatientSummary.RODocumentUpload && (
          <DocumentUploadButton
            onClick={() => {
              history.push(`/radiation/patient/${patientId}/documents/upload`);
            }}
          />
        )}
      </HeadingBlock>
      <StyledDocListCol ref={docsListRef}>
        {documents?.length > 0 ? (
          <StyledSimpleTable
            header={HEADING}
            onRowClick={(id: string) => {
              setSelectedDocument(documents.filter((doc: any) => doc.id === id)[0]);
            }}
            dataProps={TABLE_HEADER}
            data={documents}
            partitionNeedsAttentionFirst
            documentEditModes={documentEditModes}
            selectedRow={selectedDocument ? selectedDocument.id : null}
            renderRowIcon={renderDocumentRowIcon}
            renderRowTooltip={renderDocumentRowTooltip}
            renderRowStatus={(doc: any) => {
              if (doc.status === 'Draft') {
                return (
                  <Typography variant="caption" color="warning.dark">
                    Draft
                  </Typography>
                );
              }
              const showChip =
                doc.status === 'Approved' &&
                (doc.id in documentEditModes ? documentEditModes[doc.id] : doc.hasUncommittedChanges);
              if (!showChip) return null;
              return (
                <Typography variant="caption" color="warning.dark">
                  Unsubmitted changes
                </Typography>
              );
            }}
          />
        ) : (
          <Stack alignItems="center" paddingTop={4}>
            <FindInPageOutlinedIcon fontSize="large" color="primary" />
            <Typography variant="body1">{props.emptyStateMessage}</Typography>
          </Stack>
        )}
      </StyledDocListCol>
    </StyledContainer>
  );
};

export default DocumentsList;
